import { put, select, takeEvery, all } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { ITerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';
import { getCatalogsTerritorialUnitsSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import {
  createAgreementFilter,
  deleteAgreementFilter,
  fetchAgreementFilterById,
  fetchAgreementFilters,
  updateAgreementFilter,
} from 'APIPackage/AgreementListModule/services/agreementListApi';
import { IAgreementListFilterRaw } from 'APIPackage/AgreementListModule/interfaces/IAgreementListFilterRaw';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet, catalogKnmTypes248Get } from 'CorePackage/CatalogsModule/actions/catalogsActions';

import { AgreementListFilterActionsEnum } from '../enums/AgreementListFilterActionsEnum';
import {
  agreementListFilterCreate,
  agreementListFilterGetData,
  agreementListFilterGetMeta,
  agreementListFilterRemove,
  agreementListFilterSetCurrent,
  agreementListFilterSetData,
  agreementListFilterSetMeta,
  agreementListFilterUpdate,
} from '../actions/agreementListFilterActions';
import { mapRawToAgreementFilter } from '../common/mapRawToAgreementFilter';
import { mapAgreementFilterToRaw } from '../common/mapAgreementFilterToRaw';
import { IAgreementListFilter } from '../interfaces/IAgreementListFilter';

export function* watchAgreementListFilterSaga() {
  yield takeEvery(AgreementListFilterActionsEnum.AGREEMENT_LIST_FILTER_INIT, agreementListFilterInitSaga);
  yield takeEvery(AgreementListFilterActionsEnum.AGREEMENT_LIST_FILTER_GET_META, agreementListFilterGetMetaSaga);
  yield takeEvery(AgreementListFilterActionsEnum.AGREEMENT_LIST_FILTER_GET_DATA, agreementListFilterGetDataSaga);
  yield takeEvery(AgreementListFilterActionsEnum.AGREEMENT_LIST_FILTER_CREATE, agreementListFilterCreateSaga);
  yield takeEvery(AgreementListFilterActionsEnum.AGREEMENT_LIST_FILTER_UPDATE, agreementListFilterUpdateSaga);
  yield takeEvery(AgreementListFilterActionsEnum.AGREEMENT_LIST_FILTER_REMOVE, agreementListFilterRemoveSaga);
}

const agreementListPageCatalogs: CommonCatalogsNamesEnum[] = [
  CommonCatalogsNamesEnum.KNM_YEARS,
  CommonCatalogsNamesEnum.TERRITORIAL_UNITS,
];

function* agreementListFilterInitSaga() {
  yield all(agreementListPageCatalogs.map(catalogName => put(catalogGet(catalogName))));
  yield put(catalogKnmTypes248Get({}));
  yield put(agreementListFilterGetMeta());
}

function* agreementListFilterGetMetaSaga() {
  function* funcTry() {
    const meta: IFilterResult[] = yield fetchAgreementFilters();

    yield put(agreementListFilterSetMeta(meta));
    const current = getStorageItem<IAgreementListFilter>(StorageNamesEnum.AGREEMENT_LIST_FILTER);

    const currentId = meta.find(m => m.name === current?.name)?.id;

    yield put(agreementListFilterSetCurrent({ current, currentId }));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* agreementListFilterGetDataSaga(action: ActionType<typeof agreementListFilterGetData>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const territorialUnitsSelectOptions: ITerritorialUnitSelectOption[] = getCatalogsTerritorialUnitsSelectOptions(
      state,
    );

    const { filters } = state.agreementListFilter;

    if (filters.find(f => f.meta.id === id)?.data !== undefined) {
      return;
    }

    const raw: IAgreementListFilterRaw = yield fetchAgreementFilterById(id);
    const filter = mapRawToAgreementFilter({ raw, territorialUnitsSelectOptions });

    yield put(agreementListFilterSetData(id, filter));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* agreementListFilterCreateSaga(action: ActionType<typeof agreementListFilterCreate>) {
  function* funcTry() {
    const { data } = action.payload;
    const state: IRootState = yield select();
    const rawFilter = mapAgreementFilterToRaw(data);
    const meta: IFilterResult = yield createAgreementFilter(rawFilter);
    const metaList = state.agreementListFilter.filters.map(f => f.meta);

    yield put(agreementListFilterSetMeta([...metaList, meta]));
    yield put(agreementListFilterSetData(meta.id, data));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* agreementListFilterUpdateSaga(action: ActionType<typeof agreementListFilterUpdate>) {
  function* funcTry() {
    const { id, data } = action.payload;
    const rawFilter = mapAgreementFilterToRaw(data);

    yield updateAgreementFilter(id, rawFilter);
    yield put(agreementListFilterSetData(id, data));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* agreementListFilterRemoveSaga(action: ActionType<typeof agreementListFilterRemove>) {
  yield sagaWrapper({
    *funcTry() {
      const { id } = action.payload;

      yield deleteAgreementFilter(id);
    },
  });
}
